import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { categoryCatalog, type CategoryCatalog } from './api';
import type { FeedbackLocale } from './i18n';

/**
 * Названия категорий и причин из справочника сервера.
 *
 * В карточке обращения справочник уже подгружался и показывал «Качество мойки»,
 * «Повреждение автомобиля», а очередь и отчёты рядом печатали сырые ключи
 * wash_quality, damage, streaks. Один справочник на модуль — один язык на экранах.
 */
interface CatalogApi {
  readonly catalog: CategoryCatalog | null;
  readonly categoryName: (code: string | null) => string;
  readonly reasonName: (code: string | null) => string;
}

const CatalogContext = createContext<CatalogApi | undefined>(undefined);

export function CategoryCatalogProvider({
  locale,
  children,
}: {
  locale: FeedbackLocale;
  children: ReactNode;
}) {
  const [catalog, setCatalog] = useState<CategoryCatalog | null>(null);

  useEffect(() => {
    void categoryCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
  }, []);

  const api = useMemo<CatalogApi>(() => {
    const categories = new Map(
      (catalog?.categories ?? []).map((c) => [c.code, locale === 'kk' ? c.nameKk : c.nameRu]),
    );
    const reasons = new Map(
      (catalog?.reasons ?? []).map((r) => [r.code, locale === 'kk' ? r.nameKk : r.nameRu]),
    );
    return {
      catalog,
      // Пока справочник не приехал, показываем код — это честнее пустого места.
      categoryName: (code) => (code ? (categories.get(code) ?? code) : '—'),
      reasonName: (code) => (code ? (reasons.get(code) ?? code) : '—'),
    };
  }, [catalog, locale]);

  return <CatalogContext.Provider value={api}>{children}</CatalogContext.Provider>;
}

export function useCategoryCatalog(): CatalogApi {
  const api = useContext(CatalogContext);
  if (!api) {
    return {
      catalog: null,
      categoryName: (code) => code ?? '—',
      reasonName: (code) => code ?? '—',
    };
  }
  return api;
}
